import React from 'react';
import { AlertTriangle, CheckCircle2, Info } from 'lucide-react';

// What /api/load-check found for the file about to be loaded: each sheet against the
// previous loads of its file template, and the file against a whole usual week.
function LoadCheckPanel({ check, loading, error, confirmed, onConfirm }) {
  if (loading) {
    return (
      <div className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-300">
        <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        Comparaison avec l'historique de cette enseigne...
      </div>
    );
  }
  if (error || (check && !check.checked)) {
    return (
      <div className="flex items-start gap-3 p-4 rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-300">
        <Info className="w-5 h-5 shrink-0 text-gray-400" />
        Vérification par rapport à l'historique indisponible ({error || check.error}). Vérifiez les totaux avant de lancer l'import.
      </div>
    );
  }
  if (!check) return null;

  const anomalies = [
    ...check.file_anomalies,
    ...check.sheets.flatMap((sheet) => sheet.anomalies),
  ];
  const newSheets = check.sheets.filter((sheet) => sheet.history_loads === 0).map((sheet) => sheet.sheet);

  if (anomalies.length === 0) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-3 p-4 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 text-sm text-green-800 dark:text-green-300">
          <CheckCircle2 className="w-5 h-5 shrink-0" />
          Les chiffres de ce fichier sont dans la norme des chargements précédents.
        </div>
        {newSheets.length > 0 && <NewSheetsNote sheets={newSheets} />}
      </div>
    );
  }

  return (
    <div className="p-4 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 space-y-3">
      <h3 className="flex items-center gap-2 font-semibold text-amber-800 dark:text-amber-300">
        <AlertTriangle className="w-5 h-5" />
        Ce fichier ne ressemble pas aux chargements précédents
      </h3>
      <ul className="ml-7 list-disc text-sm text-gray-700 dark:text-gray-200 space-y-1">
        {anomalies.map((anomaly) => (
          <li key={`${anomaly.sheet}-${anomaly.feature}`}>
            {anomaly.sheet && <span className="font-medium">{anomaly.sheet} · </span>}
            {anomaly.message}
          </li>
        ))}
      </ul>
      <p className="ml-7 text-xs text-gray-600 dark:text-gray-400">
        Vérifiez le fichier, la semaine et le mapping (colonnes de ventes et de stock, onglets chargés).
      </p>
      {newSheets.length > 0 && <NewSheetsNote sheets={newSheets} />}
      <label className="ml-7 flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-100 cursor-pointer">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => onConfirm(e.target.checked)}
          className="w-4 h-4 accent-amber-600"
        />
        J'ai vérifié, ces chiffres sont corrects
      </label>
    </div>
  );
}

function NewSheetsNote({ sheets }) {
  return (
    <p className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
      <Info className="w-4 h-4 shrink-0" />
      Aucun chargement précédent de ce modèle de fichier pour {sheets.join(', ')} (nouveau fichier ou onglet renommé) : rien à comparer, vérifiez les totaux.
    </p>
  );
}

export default LoadCheckPanel;
